import { AxiosRequestConfig } from "axios";
import { httpService } from "./HttpServices";
import { ApiResponse } from "../utils/APIResponse";

export interface NotificationReadCount {
  unreadCount: number;
}

export enum NotificationType {
  Invitation = "invitation",
  SyncError = "sync_error",
  MemberAdded = "member_added",
  MemberRemoved = "member_removed",
  MemberActivated = "member_activated",
  MemberDeactivated = "member_deactivated",
  RoleChanged = "role_changed",
  OwnershipTransferred = "ownership_transferred",
  OrganizationDeleted = "organization_deleted",
  OrganizationLeft = "organization_left",
  InvitationAccepted = "invitation_accepted",
  InvitationDeclined = "invitation_declined",
  InvitationRevoked = "invitation_revoked",
  DataExportReady = "data_export_ready",
  SecurityAlert = "security_alert",
}

export enum NotificationPriority {
  High = "high",
  Normal = "normal",
}

export interface InvitationNotificationMetadata {
  invitationId: string;
  organizationId: string;
  inviteToken?: string;
  invitationStatus?: "pending" | "accepted" | "declined" | "expired";
}

export interface MemberNotificationMetadata {
  organizationId: string;
  organizationName?: string;
  memberId?: string;
  memberEmail?: string;
  memberName?: string;
  role?: string;
  previousRole?: string;
}

export interface OwnershipTransferNotificationMetadata {
  organizationId: string;
  organizationName?: string;
  previousOwnerId?: string;
  previousOwnerEmail?: string;
  newOwnerId?: string;
  newOwnerEmail?: string;
}

export interface OrganizationNotificationMetadata {
  organizationId: string;
  organizationName?: string;
}

export interface InvitationStatusNotificationMetadata {
  invitationId: string;
  organizationId: string;
  organizationName?: string;
  inviterId?: string;
  inviterEmail?: string;
  inviterName?: string;
}

export interface DataExportNotificationMetadata {
  exportId?: string;
  downloadUrl?: string;
  expiresAt?: string;
}

export interface SecurityAlertNotificationMetadata {
  alertType: string;
  ipAddress?: string;
  userAgent?: string;
  timestamp?: string;
  details?: Record<string, any>;
}

export interface SyncErrorNotificationMetadata {
  organizationId: string;
  fieldName: string;
  objectType: string;
  errorMessage: string;
  errorType: "field_locked" | "field_deleted" | "field_changed" | "other";
  errorCount?: number;
  hubspotObjectId?: string;
  productObjectId?: string;
}

export interface Notification {
  _id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  metadata: Record<string, any>;
  read: boolean;
  priority: NotificationPriority;
  createdAt: string;
  updatedAt?: string;
}

export interface GetNotificationsParams {
  offset?: number;
  limit?: number;
  priority?: "high" | "normal";
}

export interface GetNotificationsResponse {
  notifications: Notification[];
  totalCount: number;
  offset: number;
  limit: number;
  hasMore: boolean;
}

export interface MarkAsReadResponse {
  matched: number;
  modified: number;
}

export interface MarkAllAsReadResponse {
  message: string;
}

export class NotificationService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = "/users/notifications";
  }

  async getNotificationUnreadCount(
    config?: AxiosRequestConfig
  ): Promise<{ data: NotificationReadCount; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<NotificationReadCount>
      >(`${this.baseUrl}/unread-count`, config);

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      console.error("Error fetching notification unread count:", error);
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch notification unread count.";
      throw new Error(errorMessage);
    }
  }

  async getNotifications(
    params?: GetNotificationsParams,
    config?: AxiosRequestConfig
  ): Promise<{ data: GetNotificationsResponse; message: string }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.offset !== undefined) {
        queryParams.append("offset", String(params.offset));
      }
      if (params?.limit !== undefined) {
        queryParams.append("limit", String(params.limit));
      }
      if (params?.priority) {
        queryParams.append("priority", params.priority);
      }

      const url = `${this.baseUrl}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await httpService.get<ApiResponse<GetNotificationsResponse>>(
        url,
        config
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      console.error("Error fetching notifications:", error);
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch notifications.";
      throw new Error(errorMessage);
    }
  }

  async markAsRead(
    notificationIds: string | string[],
    config?: AxiosRequestConfig
  ): Promise<{ data: MarkAsReadResponse; message: string }> {
    try {
      const response = await httpService.patch<ApiResponse<MarkAsReadResponse>>(
        `${this.baseUrl}/mark-as-read`,
        { notificationIds },
        config
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to mark notification(s) as read.";
      throw new Error(errorMessage);
    }
  }

  async markAllNotificationsAsRead(
    config?: AxiosRequestConfig
  ): Promise<{ data: MarkAllAsReadResponse; message: string }> {
    try {
      const response = await httpService.patch<ApiResponse<MarkAllAsReadResponse>>(
        `${this.baseUrl}/mark-all-as-read`,
        {},
        config
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to mark notifications as read.";
      throw new Error(errorMessage);
    }
  }
}

export const notificationService = new NotificationService();
